import { HStack, ButtonGroup } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { useSetActiveEventPanelTab } from '../active-event-panel-tab-provider';
import { EventPanelFormType } from '@mark43/cad-api';
import { useRefreshEventPanel } from '../refresh-event-panel';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

const CreateEventBaseFooter = observer(function CreateEventBaseFooter() {
    const modalControls = useFullscreenModalControls();
    const workspacePanel = useWorkspacePanel();
    const refreshEventPanel = useRefreshEventPanel();
    const eventForm = useIntegratedForm<EventPanelFormType>();
    const { eventPanelConfiguration, eventPanelOptions } = useBootstrapState();
    const setActiveEventPanelTab = useSetActiveEventPanelTab();
    const handleScheduleSuccess = useCallback(() => {
        setActiveEventPanelTab(undefined);
    }, [setActiveEventPanelTab]);
    const enabledFeatures = useEnabledFeatures();
    const handleCreateEventSuccess = useCallback(() => {
        if (enabledFeatures.openRelatedDataTabByDefault) {
            setActiveEventPanelTab('RELATED_DATA');
        } else {
            setActiveEventPanelTab('CAUTIONS');
        }
    }, [enabledFeatures.openRelatedDataTabByDefault, setActiveEventPanelTab]);
    return (
        <HStack w="100%" justifyContent="flex-end">
            <ButtonGroup>
                <FormSaveButton
                    variant="ghost"
                    onClick={() => {
                        workspacePanel.notifyActivity();
                        eventForm.form.trigger().then((passedValidation) => {
                            const cfsValue = eventForm.form.getValues().callForService;
                            if (cfsValue && cfsValue.agencyTypeGlobalAttrIds.length > 1) {
                                eventForm.form.setError('callForService', {
                                    type: 'validation',
                                    message: 'Cannot schedule an event with a combined CFS',
                                });
                            }
                            if (passedValidation && eventPanelOptions) {
                                modalControls.open({
                                    type: 'SCHEDULE_EVENT',
                                    props: {
                                        form: eventForm.form,
                                        eventPanelOptions,
                                        refreshEventPanel,
                                        workspacePanel,
                                        onSuccess: handleScheduleSuccess,
                                    },
                                });
                            }
                        });
                    }}
                >
                    Schedule
                </FormSaveButton>
                <FormSaveButton
                    variant="outline"
                    onClick={(e) => {
                        workspacePanel.notifyActivity();
                        eventForm.form.setValue(
                            'eventCommunication',
                            eventPanelConfiguration.eventCommunicationEmergency ?? null
                        );
                        eventForm.onSubmit(e, handleCreateEventSuccess);
                    }}
                >
                    Transmit
                </FormSaveButton>
                <FormSaveButton
                    onClick={(e) => {
                        workspacePanel.notifyActivity();
                        eventForm.form.setValue(
                            'eventCommunication',
                            eventPanelConfiguration.eventCommunicationSave ?? null
                        );
                        eventForm.onSubmit(e, handleCreateEventSuccess);
                    }}
                >
                    Save
                </FormSaveButton>
            </ButtonGroup>
        </HStack>
    );
});

const CreateAndClearEventFooter = observer(function CreateAndClearEventFooter() {
    const eventForm = useIntegratedForm<EventPanelFormType>();
    const eventPanelConfiguration = useBootstrapState().eventPanelConfiguration;
    const workspacePanel = useWorkspacePanel();
    return (
        <HStack w="100%" justifyContent="flex-end">
            <FormSaveButton
                onClick={(e) => {
                    workspacePanel.notifyActivity();
                    eventForm.form.setValue(
                        'eventCommunication',
                        eventPanelConfiguration?.eventCommunicationSave ?? null
                    );
                    eventForm.onSubmit(e, () => {
                        workspacePanel.close();
                    });
                }}
            >
                Save & Clear
            </FormSaveButton>
        </HStack>
    );
});

export const CreateEventFooter = observer(function CreateEventFooter() {
    const eventForm = useIntegratedForm<EventPanelFormType>();
    const eventPanelOptions = useBootstrapState().eventPanelOptions;
    const clearEventPrimaryDisposition = useWatch({
        name: 'clearEventPrimaryDisposition',
        control: eventForm.form.control,
    });
    if (!eventPanelOptions) {
        return null;
    }
    const showCreateAndClearEventFooter = !!clearEventPrimaryDisposition;
    return showCreateAndClearEventFooter ? (
        <CreateAndClearEventFooter />
    ) : (
        <CreateEventBaseFooter />
    );
});
